import { useCallback, useContext } from 'react'
import { collection, doc, runTransaction, updateDoc } from 'firebase/firestore'
import { firebase_firestore } from '../common/firebaseConfig'
import { PatternContext } from '../app/builder/contexts/PatternContext'
import {
  COMPOSITE_LIMITS,
  CompositeNode,
  CompositeOutput,
  CustomIndicatorDoc,
} from '../types/composite'
import { canonicalizeDef, validateComposite } from '../common/utils/composite'
import { captureEvent } from '../posthog'

function useSaveCustomIndicator({ gcid, tradingHouse }: { gcid?: string; tradingHouse?: string }) {
  const { getIndicatorByValue, customIndicators } = useContext(PatternContext)

  // Publishing is copy-on-write: every publish creates a new immutable version
  // doc and updates the parent's denormalized latest defJson. The transaction
  // keeps the version counter race-free across concurrent sessions.
  const publish = useCallback(
    async (draft: {
      id?: string
      name: string
      nodes: CompositeNode[]
      outputs: CompositeOutput[]
      scale: 'overlay' | 'oscillator'
    }): Promise<CustomIndicatorDoc> => {
      if (!gcid || !tradingHouse) throw new Error('Not signed in')
      // Enforced at the write path, not just the input's maxLength: agent
      // drafts and Duplicate's " copy" suffix bypass the UI cap
      const name = draft.name.trim().slice(0, COMPOSITE_LIMITS.maxNameLength)
      if (!name) throw new Error('Name is required')
      // Deactivated indicators don't occupy publish slots (there is no hard
      // delete, so an all-docs count would lock users out permanently)
      const activeCount = customIndicators.filter((i) => i.active !== false).length
      if (!draft.id && activeCount >= COMPOSITE_LIMITS.maxPerUser) {
        throw new Error(`You can have at most ${COMPOSITE_LIMITS.maxPerUser} custom indicators`)
      }
      const validation = validateComposite(draft.nodes, draft.outputs, getIndicatorByValue)
      if (!validation.valid) {
        throw new Error(
          validation.issues.find((i) => i.level === 'error')?.message ?? 'Invalid indicator'
        )
      }
      const col = collection(firebase_firestore, 'customIndicators')
      const id = draft.id ?? doc(col).id
      const parentRef = doc(col, id)

      const published = await runTransaction(firebase_firestore, async (tx) => {
        const existing = await tx.get(parentRef)
        const version = ((existing.data()?.latestVersion as number) ?? 0) + 1
        const def = canonicalizeDef({
          id,
          version,
          name,
          nodes: draft.nodes,
          outputs: draft.outputs,
          scale: draft.scale,
        })
        // The canonical string is the only stored/embedded/hashed form of the
        // def (see CustomIndicatorDoc.defJson).
        const defJson = JSON.stringify(def)
        // Firestore index entries cap at ~7.5 KiB; unbounded min/max arity can
        // theoretically push past it
        // Byte length, not UTF-16 units — the Firestore index cap is in bytes
        if (new TextEncoder().encode(defJson).length > 7000) {
          throw new Error('This indicator is too complex to save — remove some operands')
        }
        const now = Date.now()
        const stored = {
          id,
          name,
          gcid,
          tradingHouse,
          // Editing must not resurrect a deliberately deactivated indicator
          // nor flip a visibility choice
          active: (existing.data()?.active as boolean | undefined) ?? true,
          visibility:
            (existing.data()?.visibility as 'public' | 'private' | undefined) ?? 'private',
          latestVersion: version,
          defJson,
          created: (existing.data()?.created as number) ?? now,
          updated: now,
        }
        // Deliberate full overwrite: the client owns every field on the parent
        // doc (the schema is fully enumerated in `stored`). Backend writers
        // must not denormalize extra fields onto this doc — a republish would
        // silently drop them. Denormalized/server-owned data belongs in a
        // sibling doc or subcollection.
        tx.set(parentRef, stored)
        tx.set(doc(collection(parentRef, 'versions'), String(version)), {
          version,
          defJson,
          created: now,
        })
        return { ...stored, def }
      })
      captureEvent('custom_indicator_published', {
        is_new: !draft.id,
        node_count: draft.nodes.length,
        output_count: draft.outputs.length,
        scale: draft.scale,
      })
      return published
    },
    [gcid, tradingHouse, getIndicatorByValue, customIndicators]
  )

  // Pattern-style create-on-entry: a brand-new indicator is REAL from the
  // first click — v1 published immediately (mirrors defaultPattern +
  // forceSave). The def is the minimal valid composite (Close as a single
  // math step), the analogue of a pattern's empty condition groups: every
  // published version must validate for the engines, so "empty" is not an
  // option.
  const createIndicator = useCallback(async (): Promise<CustomIndicatorDoc> => {
    const stepId = 'n1'
    return publish({
      name: 'New Indicator',
      nodes: [{ id: stepId, kind: 'math', expr: { t: 'ref', ref: { source: 'close' } } }],
      outputs: [{ field: 'value1', ref: { node: stepId } }],
      scale: 'overlay',
    })
  }, [publish])

  const setActive = useCallback(
    async (id: string, active: boolean) => {
      if (!gcid) throw new Error('Not signed in')
      await updateDoc(doc(firebase_firestore, `customIndicators/${id}`), {
        active,
        updated: Date.now(),
      })
    },
    [gcid]
  )

  const setVisibility = useCallback(
    async (id: string, visibility: 'public' | 'private') => {
      if (!gcid) throw new Error('Not signed in')
      await updateDoc(doc(firebase_firestore, `customIndicators/${id}`), {
        visibility,
        updated: Date.now(),
      })
    },
    [gcid]
  )

  return { publish, createIndicator, setActive, setVisibility }
}

export default useSaveCustomIndicator
